import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Subscription } from 'rxjs';

import { FreeAgentsInterface } from '@interfaces/free-agents.interface';
import { FreeAgentsService } from '@services/free-agents.service';

import { BaseComponent } from '../base.component';

// --- Free agent row types
type FreeAgentItem = FreeAgentsInterface['free_agents'][number];

type FreeAgentRow = {
    item: FreeAgentItem;
    playerId: number | null;
    playerName: string;
    playerPosition: string;
    playerHeight: string | null;
    playerWeight: string | null;
    playerLink: (string | number)[];
    highSchool: string | null;
    college: string | null;
};

@Component({
    selector: 'app-free-agents',
    templateUrl: './free-agents.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreeAgentsComponent extends BaseComponent implements OnDestroy, OnInit {
    @Input()
    public sportId: number;

    @Input()
    public sportName: string;

    @Input()
    public tournamentId: number;

    public year: number | null;

    public subscriber: Subscription | null = null;
    public data: FreeAgentsInterface | null = null;
    public rows: FreeAgentRow[] = [];
    public visibleRows: FreeAgentRow[] = [];
    private batchSize = 50;
    private observer: IntersectionObserver | null = null;
    private isLoadingNextBatch = false;

    public hasHighSchool = false;
    public hasCollege = false;

    @ViewChild('loadMoreTrigger')
    public set loadMoreTrigger(trigger: ElementRef<HTMLDivElement> | undefined) {
        if (!trigger) {
            this.observer?.disconnect();
            return;
        }

        this.setupObserver(trigger);
    }

    public constructor(
        private freeAgentsService: FreeAgentsService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.load();
    }

    public ngOnDestroy(): void {
        this.subscriber?.unsubscribe();
        this.observer?.disconnect();
    }

    private async load(): Promise<any> {
        try {
            this.subscriber?.unsubscribe();

            const data = await this.freeAgentsService.getFreeAgents(
                this.tournamentId,
                this.year,
            );

            this.subscriber = data.subscribe((data: FreeAgentsInterface) => {
                this.year = data.year ?? null;
                this.data = data;
                this.rows = data.free_agents.map(a => this.mapRow(a));
                this.resetVisibleRows();

                this.hasHighSchool = this.rows.some(
                    row => !!row.highSchool
                );

                this.hasCollege = this.rows.some(
                    row => !!row.college
                );
                this.cdr.markForCheck();
            });
        } catch (e) {
            // console.log(e);
        }
    }

    public onYearChange(e: Event): void {
        this.year = +((e.target as HTMLInputElement).value);
        this.load();
    }

    public trackByFreeAgent(index: number, row: FreeAgentRow): number {
        return row.playerId ?? index;
    }

    public loadNextBatch(): void {
        if (this.isLoadingNextBatch) {
            return;
        }

        const currentLength = this.visibleRows.length;
        const next = this.rows.slice(currentLength, currentLength + this.batchSize);

        if (next.length === 0) {
            return;
        }

        this.isLoadingNextBatch = true;
        this.visibleRows = [...this.visibleRows, ...next];
        this.isLoadingNextBatch = false;
        this.cdr.markForCheck();
    }
    private setupObserver(trigger: ElementRef<HTMLDivElement>): void {
        this.observer?.disconnect();

        this.observer = new IntersectionObserver(entries => {
            const isVisible = entries.some(entry => entry.isIntersecting);

            if (!isVisible || !this.hasMoreRows()) {
                return;
            }

            this.loadNextBatch();
        }, {
            root: null,
            rootMargin: '200px 0px',
            threshold: 0.1,
        });

        this.observer.observe(trigger.nativeElement);
    }

    private resetVisibleRows(): void {
        this.visibleRows = this.rows.slice(0, this.batchSize);
    }

    public hasMoreRows(): boolean {
        return this.visibleRows.length < this.rows.length;
    }

    private mapRow(a: FreeAgentItem): FreeAgentRow {
        return {
            item: a,
            playerId: a.player?.id ?? null,
            playerName: a.player?.name ?? '',
            playerPosition: this.toTitleCase(a.player?.position),
            playerHeight: this.formatUnitValue(a.player?.height, 'cm'),
            playerWeight: this.formatUnitValue(a.player?.weight, 'kg'),
            playerLink: [
                '/page/sport/player',
                `${this.sportName}-${this.sportId}`,
                a.player?.id ?? 0,
            ],
            highSchool: a.player?.high_school ?? null,
            college: a.player?.college ?? null,
        };
    }

    private formatUnitValue(value: string | number | null | undefined, unit: string): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) {
            return `${value} ${unit}`;
        }

        const isImperial = navigator.language.includes('US');

        if (unit === 'cm') {
            if (!isImperial) {
                return `${value} ${unit}`;
            }

            return `${(numericValue * 0.393701).toFixed(2)} in`;
        }

        if (unit === 'kg') {
            if (!isImperial) {
                return `${value} ${unit}`;
            }

            return `${(numericValue * 2.20462).toFixed(2)} lb`;
        }

        return `${value} ${unit}`;
    }

    private toTitleCase(value: string | null | undefined): string {
        if (!value) {
            return '';
        }

        return value.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
    }

    protected readonly console = console;
}
